import { useEffect, useMemo, useRef, useState } from "react";
import {
  DocumentTextIcon,
  ListBulletIcon,
  GlobeAltIcon,
  FilmIcon,
  SquaresPlusIcon,
  PhoneIcon,
  TableCellsIcon,
  UserGroupIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  PresentationChartLineIcon,
  RectangleGroupIcon,
  DocumentDuplicateIcon,
  IdentificationIcon,
  EllipsisHorizontalCircleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  UserCircleIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  WrenchIcon,
  DocumentMagnifyingGlassIcon,
  CalendarDaysIcon,
  ClockIcon,
  AdjustmentsHorizontalIcon,
  CurrencyYenIcon,
} from "@heroicons/react/24/outline";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { baseColor } from "..";
import { Company } from "../model/Companies";
import companiesQuery from "../firestore/Companies.query";
import { AlertValue, alertOpen } from "../features/alert.slice";
import { ConstMessage, SessionKey } from "../config/const";
import { useDispatch } from "react-redux";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const style = {
  color: {
    blue: `bg-blue-50 border-blue-600 text-blue-600`,
    green: `bg-green-50 border-green-600 text-green-600`,
    red: `bg-red-50 border-red-600 text-red-600`,
    yellow: `bg-yellow-50 border-yellow-600 text-yellow-600`,
    indigo: `bg-indigo-50 border-indigo-600 text-indigo-600`,
  },
};

export const Sidebar = () => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  const tenantId = currentUser?.tenantId;
  const dispatch = useDispatch();

  const [company, setCompany] = useState<Company>();

  const getInitialState = () => {
    const savedState = sessionStorage.getItem(SessionKey.SIDEBAR_STATE);
    const result = savedState ? JSON.parse(savedState) : {};
    return result;
  };

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(getInitialState);

  const upIcon = {icon: ChevronUpIcon};
  const downIcon = {icon: ChevronDownIcon};

  const navigate = useNavigate();
  const location = useLocation();
  const auhtLogout = async () => {
    await auth.signOut().then(() => {
      console.log("Sign-out successful.")
      document.title = `イベントサポート`;
      sessionStorage.clear();
      navigate("/login");
    })
    .catch(err => {
      console.error(err.message)
    })
  };

  const selectStyles = (to: string) => {
    const pathname = location.pathname;
    const subMenuPathname = pathname.includes('/') ? `/${pathname.split('/')[1]}` : pathname;
    if (to === "/") {
      return  subMenuPathname === to
        ? style.color[baseColor]
        : "border-transparent text-white hover:bg-gray-50 hover:text-gray-900";
    } else if (subMenuPathname == to || (pathname.match("/settings/") && to.match("/settings/"))) {
      return style.color[baseColor];
    } else {
      return "border-transparent text-white hover:bg-gray-50 hover:text-gray-900";
    }
  };

  useMemo(() => {
    const f = async () => {
      if(currentUser?.tenantId !== undefined){
        // 団体情報
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);
        document.title = `イベントサポート ${company.name}`
      }
    };
    f();
  }, [currentUser?.tenantId]);

  useEffect(() => {
    const f = async () => {
      try{
        if(location.pathname === "/"){
          navigate("/schedule");
        }
        for(let i = 0; i < navigation.length; i++){
          const subMenu = navigation[i].subMenu;
          for(let idx = 0; idx < subMenu.length; idx++){
            if(subMenu[idx].href === location.pathname){
              setOpenMenus((prev) => ({ ...prev, [navigation[i].id]: true }));
            }
          }
        }
      } catch(err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
    };
    f();
  },[location.pathname, navigate]);

  const messageNavi = [
    { name: "お知らせ", href: "/message", icon: ChatBubbleLeftIcon, displayFlg: true, marginFlg: false},
    { name: "予定表", href: "/schedule", icon: CalendarIcon, displayFlg: true },
    { name: "Excel取込", href: "/messageRegister", icon: DocumentDuplicateIcon, displayFlg: true },
  ];
  const userNavi = [
    { name: "利用者一覧", href: "/userList", icon: UserGroupIcon, displayFlg: true, marginFlg: false },
    { name: "グループ一覧", href: "/groupList", icon: RectangleGroupIcon, displayFlg: true },
    { name: "利用者IDカード出力", href: "/usersIdcardCreate", icon: IdentificationIcon, displayFlg: sessionStorage.getItem(SessionKey.USER_ID_CARD_AUTH) === 'true' },
    { name: "利用状況", href: "/usageSituation", icon: PresentationChartLineIcon, displayFlg: true },
  ];
  const attendanceNavi = [
    { name: "イベント参加履歴一覧", href: "/eventParticipation", icon: CalendarDaysIcon, displayFlg: true, marginFlg: false },
  ];
  const pointNavi = [
    //{ name: "基本設定", href: "", icon: WrenchIcon, displayFlg: true },
    //{ name: "詳細表示", href: "", icon: DocumentMagnifyingGlassIcon, displayFlg: true },
    { name: "ポイント設定一覧", href: "/pointAwardedSettings", icon: DocumentMagnifyingGlassIcon, displayFlg: sessionStorage.getItem(SessionKey.MANAGE_POINT_AUTH) === 'true' },
    { name: "ポイント履歴", href: "/pointHistoryList", icon: ClockIcon, displayFlg: sessionStorage.getItem(SessionKey.MANAGE_POINT_AUTH) === 'true' },
    { name: "ポイント調整", href: "/pointAdjustmentPage", icon: AdjustmentsHorizontalIcon, displayFlg: sessionStorage.getItem(SessionKey.MANAGE_POINT_AUTH) === 'true' },
    { name: "調整履歴", href: "/PointAdjustmentHistory", icon: ClockIcon, displayFlg: sessionStorage.getItem(SessionKey.MANAGE_POINT_AUTH) === 'true', marginFlg: true},
  ];
  const municipalitiesNavi = [
    { name: "契約情報", href: "/municipalitiesInfo", icon: DocumentTextIcon, displayFlg: true, marginFlg: false },
    { name: "オプション契約詳細", href: `/optionContractSetting`, icon: EllipsisHorizontalCircleIcon, displayFlg: sessionStorage.getItem(SessionKey.OPTION_CONTRACT_LIST) === 'true' },
  ];
  const appSettingNavi = [
    { name: "問い合わせ先設定", href: "/contactInformationSetting", icon: PhoneIcon, displayFlg: true, marginFlg: false },
    { name: "動画メニュー設定", href: "/movieMenuSetting", icon: FilmIcon, displayFlg: true },
    { name: "トップ画面設定", href: "/menuSettings", icon: ListBulletIcon, displayFlg: true },
    { name: "連携ボタンメニュー設定", href: "/linkMenus", icon: TableCellsIcon, displayFlg: true },
    { name: "ミーティングURL設定", href: "/onlineUrlSetting", icon: GlobeAltIcon, displayFlg: true },
    { name: "返信ボタン設定", href: "/replyButtons", icon: SquaresPlusIcon, displayFlg: true },
  ];

  const navigation = [
    { id: "message", name: "お知らせ", icon: ChatBubbleLeftIcon, subMenu: messageNavi, displayFlg: true },
    { id: "user", name: "利用者管理", icon: UserCircleIcon, subMenu: userNavi, displayFlg: true },
    { id: "attendance", name: "出欠管理", icon: ClipboardDocumentCheckIcon, subMenu: attendanceNavi, displayFlg: true },
    { id: "point", name: "ポイント管理", icon: CurrencyYenIcon, subMenu: pointNavi, displayFlg: sessionStorage.getItem(SessionKey.MANAGE_POINT_AUTH) === 'true' },
    { id: "municipalities", name: "契約情報", icon: DocumentTextIcon, subMenu: municipalitiesNavi, displayFlg: true },
    { id: "appSetting", name: "アプリ設定", icon: Cog6ToothIcon, subMenu: appSettingNavi, displayFlg: true },
  ];

  useEffect(() => {
    sessionStorage.setItem(SessionKey.SIDEBAR_STATE, JSON.stringify(openMenus));
  }, [openMenus]);

  // メニューを開閉した時のフラグ更新
  const handleClick = (e:any, id: string) => {
    e.preventDefault();
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      <div className="flex">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="sticky flex flex-grow flex-col inset-y-0 overflow-y-auto border-r border-gray-200 bg-indigo-900 pt-5 shadow w-56 h-[100vh] flex-shrink-0">
          <div className="text-xl flex flex-shrink-0 items-center px-4 font-bold tracking-tight text-white">
            イベントサポート
          </div>
          <div className="mt-5 flex flex-grow flex-col">
            <nav className="flex-1 space-y-1 px-1 pb-4">
              {navigation.map((item) => {
                return (
                  <>
                    {item.displayFlg === undefined || item.displayFlg === true ?
                      <>
                        <a
                          key={item.name}
                          className={classNames(
                            "group flex items-center px-1 pt-2 text-sm font-medium text-white justify-between cursor-pointer"
                          )}
                          onClick={(e) => handleClick(e, item.id)}
                        >
                          <div className="flex">
                            <item.icon
                              className={classNames(
                                "mr-2 flex-shrink-0 h-5 w-5"
                              )}
                              aria-hidden="true"
                            />
                            {item.name}
                          </div>
                          {openMenus[item.id] ? 
                          <>
                            <upIcon.icon
                              className={"flex-shrink-0 h-6 w-6"}
                              aria-hidden="true"
                            />
                          </>:<>
                            <downIcon.icon
                              className={"flex-shrink-0 h-6 w-6"}
                              aria-hidden="true"
                            />
                          </>}
                        </a>
                        {openMenus[item.id] ?
                          <>
                            {item.subMenu.map((subItem) => (
                              <>
                              {subItem.displayFlg === undefined || subItem.displayFlg === true ?
                                <>
                                  <a
                                    key={subItem.name}
                                    href={subItem.href}
                                    className={classNames(
                                      selectStyles(subItem.href),
                                      "group flex items-center px-1 py-1 text-sm font-medium border-l-4 ml-5"
                                    )}
                                  >
                                    {subItem.marginFlg ? 
                                    <>
                                      <subItem.icon
                                        className={classNames(
                                          selectStyles(subItem.href),
                                          "mr-2 ml-5 flex-shrink-0 h-5 w-5"
                                        )}
                                        aria-hidden="true"
                                      />
                                    </> : 
                                    <>
                                      <subItem.icon
                                        className={classNames(
                                          selectStyles(subItem.href),
                                          "mr-2 flex-shrink-0 h-5 w-5"
                                        )}
                                        aria-hidden="true"
                                      />
                                    </>}
                                    {subItem.name}
                                  </a>
                                </>:<></>
                              } 
                            </>
                          ))}
                        </> : <></>} 
                      </> : <></>
                    }
                  </>
                )
              })}
            </nav>
          </div>
        </div>
        <div className="flex flex-col h-fit w-full">
          <div className="w-full min-w-fit">
            <div className="sticky flex top-0 z-10 h-16 flex-shrink-0 bg-indigo-50 shadow">
              <div className={"flex mx-5 flex-1"}>
                <h2 className={"self-center text-xl font-semibold text-gray-900"}>{company?.displayId}  {company?.name}</h2>
              </div>
              <div className="flex mx-5 self-center text-sm text-right">
                <span className="sr-only">Open user menu</span>
                {currentUser?.displayName}
              </div>
              <button
                type="button"
                className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                onClick={() => {if(window.confirm('ログアウトしますか？')) auhtLogout()}}
              >
                <span className="sr-only">Open sidebar</span>
                ログアウト
              </button>
            </div>
            <Outlet />
          </div>
        </div>
      </div>  
    </>
  );
};
